import { type ColorGrade, type Effect, type Transform2D } from '@cinegrid/core';
import { containScale } from './aspect';
import { createProgram, GRADE_FRAG_SHADER, VERT_SHADER } from './shaders';

export interface EffectAmounts {
  blur: number;
  sharpen: number;
  vignette: number;
  glow: number;
  noise: number;
}

export function effectAmounts(effects: Effect[] | undefined): EffectAmounts {
  const out: EffectAmounts = { blur: 0, sharpen: 0, vignette: 0, glow: 0, noise: 0 };
  if (!effects) return out;
  for (const e of effects) {
    if (!e.enabled || e.amount <= 0) continue;
    if (e.type === 'blur') out.blur = Math.max(out.blur, e.amount);
    else if (e.type === 'sharpen') out.sharpen = Math.max(out.sharpen, e.amount);
    else if (e.type === 'vignette') out.vignette = Math.max(out.vignette, e.amount);
    else if (e.type === 'glow') out.glow = Math.max(out.glow, e.amount);
    else if (e.type === 'noise') out.noise = Math.max(out.noise, e.amount);
  }
  return out;
}

export interface DrawLayer {
  texture: WebGLTexture;
  transform: Transform2D;
  color: ColorGrade;
  crossDissolve?: number;
  effects?: EffectAmounts;
  sourceSize?: { width: number; height: number };
  frameSize?: { width: number; height: number };
}

const IDENTITY_TRANSFORM: Transform2D = {
  positionX: 0,
  positionY: 0,
  scaleX: 1,
  scaleY: 1,
  rotation: 0,
  anchorX: 0.5,
  anchorY: 0.5,
  opacity: 1,
};

export class Compositor {
  readonly gl: WebGL2RenderingContext;
  readonly canvas: HTMLCanvasElement;
  private program: WebGLProgram;
  private vao: WebGLVertexArrayObject;
  private uniforms: Record<string, WebGLUniformLocation | null> = {};

  /** Offscreen scene (ping-pong for adjustment layers). */
  private sceneFbo: WebGLFramebuffer | null = null;
  private sceneTex: WebGLTexture | null = null;
  private adjFbo: WebGLFramebuffer | null = null;
  private adjTex: WebGLTexture | null = null;
  private targetW = 0;
  private targetH = 0;
  private composing = false;

  constructor(canvas: HTMLCanvasElement) {
    const gl = canvas.getContext('webgl2', {
      alpha: false,
      antialias: false,
      preserveDrawingBuffer: true,
      premultipliedAlpha: false,
    });
    if (!gl) throw new Error('WebGL2 not available');
    this.canvas = canvas;
    this.gl = gl;
    this.program = createProgram(gl, VERT_SHADER, GRADE_FRAG_SHADER);
    this.vao = this.createQuad();
    this.cacheUniforms();
  }

  private createQuad(): WebGLVertexArrayObject {
    const gl = this.gl;
    const vao = gl.createVertexArray();
    if (!vao) throw new Error('VAO failed');
    gl.bindVertexArray(vao);
    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    const data = new Float32Array([
      0, 0, 0, 0,
      1, 0, 1, 0,
      0, 1, 0, 1,
      1, 1, 1, 1,
    ]);
    gl.bufferData(gl.ARRAY_BUFFER, data, gl.STATIC_DRAW);
    const aPos = gl.getAttribLocation(this.program, 'a_pos');
    const aUv = gl.getAttribLocation(this.program, 'a_uv');
    gl.enableVertexAttribArray(aPos);
    gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 16, 0);
    gl.enableVertexAttribArray(aUv);
    gl.vertexAttribPointer(aUv, 2, gl.FLOAT, false, 16, 8);
    gl.bindVertexArray(null);
    return vao;
  }

  private cacheUniforms() {
    const names = [
      'u_tex', 'u_opacity', 'u_scale', 'u_offset', 'u_rotation', 'u_anchor', 'u_uvFlipY', 'u_sampleScreen',
      'u_lift', 'u_liftMaster', 'u_gamma', 'u_gammaMaster', 'u_gain', 'u_gainMaster',
      'u_exposure', 'u_contrast', 'u_pivot', 'u_shadows', 'u_midtones', 'u_highlights',
      'u_whites', 'u_blacks', 'u_saturation', 'u_hue', 'u_temperature', 'u_tint',
      'u_intensity', 'u_qualEnabled', 'u_hueRange', 'u_satRange', 'u_lumRange',
      'u_qualSoft', 'u_qualInvert', 'u_crossDissolve',
      'u_blur', 'u_sharpen', 'u_vignette', 'u_glow', 'u_noise', 'u_texelSize',
    ];
    for (const n of names) {
      this.uniforms[n] = this.gl.getUniformLocation(this.program, n);
    }
  }

  private makeColorTarget(w: number, h: number): { fbo: WebGLFramebuffer; tex: WebGLTexture } {
    const gl = this.gl;
    const tex = gl.createTexture();
    const fbo = gl.createFramebuffer();
    if (!tex || !fbo) throw new Error('FBO create failed');
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, w, h, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
    gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0);
    const status = gl.checkFramebufferStatus(gl.FRAMEBUFFER);
    if (status !== gl.FRAMEBUFFER_COMPLETE) {
      throw new Error(`Framebuffer incomplete: ${status}`);
    }
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    return { fbo, tex };
  }

  private ensureTargets(w: number, h: number) {
    if (this.targetW === w && this.targetH === h && this.sceneFbo && this.adjFbo) return;
    this.destroyTargets();
    const a = this.makeColorTarget(w, h);
    const b = this.makeColorTarget(w, h);
    this.sceneFbo = a.fbo;
    this.sceneTex = a.tex;
    this.adjFbo = b.fbo;
    this.adjTex = b.tex;
    this.targetW = w;
    this.targetH = h;
  }

  private destroyTargets() {
    const gl = this.gl;
    if (this.sceneFbo) gl.deleteFramebuffer(this.sceneFbo);
    if (this.adjFbo) gl.deleteFramebuffer(this.adjFbo);
    if (this.sceneTex) gl.deleteTexture(this.sceneTex);
    if (this.adjTex) gl.deleteTexture(this.adjTex);
    this.sceneFbo = this.adjFbo = null;
    this.sceneTex = this.adjTex = null;
    this.targetW = this.targetH = 0;
  }

  resize(width: number, height: number) {
    if (this.canvas.width !== width || this.canvas.height !== height) {
      this.canvas.width = width;
      this.canvas.height = height;
    }
    this.gl.viewport(0, 0, width, height);
  }

  /** Start compositing into an offscreen buffer (required for adjustment layers). */
  beginFrame(width: number, height: number) {
    this.resize(width, height);
    this.ensureTargets(width, height);
    const gl = this.gl;
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.sceneFbo);
    gl.viewport(0, 0, width, height);
    this.clear(0, 0, 0);
    this.composing = true;
  }

  /** Blit the composed scene to the visible canvas. */
  endFrame() {
    const gl = this.gl;
    if (!this.sceneFbo || !this.composing) return;
    gl.bindFramebuffer(gl.READ_FRAMEBUFFER, this.sceneFbo);
    gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, null);
    gl.viewport(0, 0, this.canvas.width, this.canvas.height);
    gl.blitFramebuffer(
      0,
      0,
      this.targetW,
      this.targetH,
      0,
      0,
      this.canvas.width,
      this.canvas.height,
      gl.COLOR_BUFFER_BIT,
      gl.NEAREST
    );
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    this.composing = false;
  }

  clear(r = 0, g = 0, b = 0) {
    const gl = this.gl;
    gl.clearColor(r, g, b, 1);
    gl.clear(gl.COLOR_BUFFER_BIT);
  }

  createTextureFromVideo(video: HTMLVideoElement): WebGLTexture {
    const gl = this.gl;
    const tex = gl.createTexture();
    if (!tex) throw new Error('texture create failed');
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, video);
    return tex;
  }

  updateTextureFromVideo(tex: WebGLTexture, video: HTMLVideoElement) {
    const gl = this.gl;
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, video);
  }

  createTextureFromCanvas(source: HTMLCanvasElement | ImageBitmap | HTMLImageElement): WebGLTexture {
    const gl = this.gl;
    const tex = gl.createTexture();
    if (!tex) throw new Error('texture create failed');
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, source);
    return tex;
  }

  createSolidTexture(color: string): WebGLTexture {
    const c = document.createElement('canvas');
    c.width = 8;
    c.height = 8;
    const ctx = c.getContext('2d');
    if (ctx) {
      ctx.fillStyle = color || '#808080';
      ctx.fillRect(0, 0, 8, 8);
    }
    return this.createTextureFromCanvas(c);
  }

  /**
   * Apply grade/FX to everything already composited under this adjustment layer.
   * Transform defines the spatial region (Premiere-style); outside stays ungraded.
   * Uses ping-pong FBOs so we never sample the texture we're rendering to.
   */
  applyAdjustment(opts: {
    color: ColorGrade;
    effects?: EffectAmounts;
    transform?: Transform2D;
  }) {
    const gl = this.gl;
    if (!this.sceneTex || !this.adjFbo || !this.adjTex || !this.sceneFbo) {
      return;
    }

    const transform = opts.transform ?? { ...IDENTITY_TRANSFORM };

    // Keep ungraded underlay outside the adjustment's transformed bounds
    gl.bindFramebuffer(gl.READ_FRAMEBUFFER, this.sceneFbo);
    gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, this.adjFbo);
    gl.blitFramebuffer(
      0,
      0,
      this.targetW,
      this.targetH,
      0,
      0,
      this.targetW,
      this.targetH,
      gl.COLOR_BUFFER_BIT,
      gl.NEAREST
    );

    // Draw graded underlay into the transformed region (screen-space UVs)
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.adjFbo);
    gl.viewport(0, 0, this.targetW, this.targetH);
    this.drawLayer(
      {
        texture: this.sceneTex,
        transform,
        color: opts.color,
        effects: opts.effects,
      },
      { blend: true, uvFlipY: true, sampleScreen: true }
    );

    // Swap: adj becomes the new scene
    const tmpFbo = this.sceneFbo;
    const tmpTex = this.sceneTex;
    this.sceneFbo = this.adjFbo;
    this.sceneTex = this.adjTex;
    this.adjFbo = tmpFbo;
    this.adjTex = tmpTex;

    // Continue drawing subsequent layers into the new scene FBO
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.sceneFbo);
    gl.viewport(0, 0, this.targetW, this.targetH);
  }

  drawLayer(
    layer: DrawLayer,
    opts?: { blend?: boolean; uvFlipY?: boolean; sampleScreen?: boolean }
  ) {
    const gl = this.gl;
    const u = this.uniforms;
    const t = layer.transform;
    const c = layer.color;
    const fx = layer.effects ?? { blur: 0, sharpen: 0, vignette: 0, glow: 0, noise: 0 };
    const blend = opts?.blend !== false;

    gl.useProgram(this.program);
    gl.bindVertexArray(this.vao);
    if (blend) {
      gl.enable(gl.BLEND);
      gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
    } else {
      gl.disable(gl.BLEND);
    }

    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, layer.texture);
    gl.uniform1i(u.u_tex, 0);
    gl.uniform1f(u.u_uvFlipY, opts?.uvFlipY ? 1 : 0);
    gl.uniform1f(u.u_sampleScreen, opts?.sampleScreen ? 1 : 0);

    const fit =
      layer.sourceSize && layer.frameSize
        ? containScale(
            layer.sourceSize.width,
            layer.sourceSize.height,
            layer.frameSize.width,
            layer.frameSize.height
          )
        : { scaleX: 1, scaleY: 1 };

    gl.uniform1f(u.u_opacity, t.opacity);
    gl.uniform2f(u.u_scale, t.scaleX * fit.scaleX, t.scaleY * fit.scaleY);
    gl.uniform2f(u.u_offset, t.positionX, t.positionY);
    gl.uniform1f(u.u_rotation, (t.rotation * Math.PI) / 180);
    gl.uniform2f(u.u_anchor, t.anchorX, t.anchorY);

    gl.uniform3f(u.u_lift, c.lift.r, c.lift.g, c.lift.b);
    gl.uniform1f(u.u_liftMaster, c.lift.master);
    gl.uniform3f(u.u_gamma, c.gamma.r, c.gamma.g, c.gamma.b);
    gl.uniform1f(u.u_gammaMaster, c.gamma.master);
    gl.uniform3f(u.u_gain, c.gain.r, c.gain.g, c.gain.b);
    gl.uniform1f(u.u_gainMaster, c.gain.master);

    gl.uniform1f(u.u_exposure, c.exposure);
    gl.uniform1f(u.u_contrast, c.contrast);
    gl.uniform1f(u.u_pivot, c.pivots);
    gl.uniform1f(u.u_shadows, c.shadows);
    gl.uniform1f(u.u_midtones, c.midtones);
    gl.uniform1f(u.u_highlights, c.highlights);
    gl.uniform1f(u.u_whites, c.whites);
    gl.uniform1f(u.u_blacks, c.blacks);

    gl.uniform1f(u.u_saturation, c.saturation);
    gl.uniform1f(u.u_hue, c.hue);
    gl.uniform1f(u.u_temperature, c.temperature);
    gl.uniform1f(u.u_tint, c.tint);
    gl.uniform1f(u.u_intensity, c.intensity);

    const q = c.qualifier;
    gl.uniform1f(u.u_qualEnabled, q.enabled ? 1 : 0);
    gl.uniform2f(u.u_hueRange, q.hueMin, q.hueMax);
    gl.uniform2f(u.u_satRange, q.satMin, q.satMax);
    gl.uniform2f(u.u_lumRange, q.lumMin, q.lumMax);
    gl.uniform1f(u.u_qualSoft, q.soft);
    gl.uniform1f(u.u_qualInvert, q.invert ? 1 : 0);
    gl.uniform1f(u.u_crossDissolve, layer.crossDissolve ?? 0);

    gl.uniform1f(u.u_blur, fx.blur);
    gl.uniform1f(u.u_sharpen, fx.sharpen);
    gl.uniform1f(u.u_vignette, fx.vignette);
    gl.uniform1f(u.u_glow, fx.glow);
    gl.uniform1f(u.u_noise, fx.noise);
    const tw = layer.sourceSize?.width || this.targetW || this.canvas.width || 1;
    const th = layer.sourceSize?.height || this.targetH || this.canvas.height || 1;
    gl.uniform2f(u.u_texelSize, 1 / tw, 1 / th);

    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    gl.enable(gl.BLEND);
  }

  destroy() {
    const gl = this.gl;
    this.destroyTargets();
    gl.deleteProgram(this.program);
    gl.deleteVertexArray(this.vao);
  }
}
